import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Button } from '../components/ui/button';

// New Gold & Black Logo
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_7d9018ae-dc6c-4832-b25d-d9d05e8d5132/artifacts/j9dbuwqr_Vector%20File.jpg';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-white border-b border-gray-100 shadow-sm" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Shield Only + Slogan */}
          <Link to="/" className="flex flex-col items-center gap-0.5" data-testid="logo-link">
            <div style={{ height: '60px', overflow: 'hidden' }}>
              <img 
                src={LOGO_URL} 
                alt="My Gear Rental" 
                style={{ height: '120px', objectFit: 'contain', objectPosition: 'center top', marginTop: '-2px' }}
              />
            </div>
            <span style={{ fontSize: '10px', color: '#333333', letterSpacing: '0.5px' }} data-testid="logo-slogan">
              Premium Gear. Trusted Journeys.
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/products" 
              className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] transition-colors"
              data-testid="nav-products"
            >
              All Gear
            </Link>
            <Link 
              to="/how-it-works" 
              className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] transition-colors"
              data-testid="nav-how-it-works"
            >
              How It Works
            </Link>
            <Link 
              to="/faq" 
              className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] transition-colors"
              data-testid="nav-faq"
            >
              FAQ
            </Link>
            <Link 
              to="/sizing-guide" 
              className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] transition-colors"
              data-testid="nav-sizing-guide"
            >
              Sizing
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <Link 
              to="/cart" 
              className="relative p-2 hover:bg-gray-100 rounded transition-colors"
              data-testid="cart-button"
            >
              <ShoppingBag className="w-5 h-5 text-gray-700" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1a1a1a] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-sm font-medium text-gray-700"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5" />
                    <span className="hidden sm:inline">{user?.name}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="w-full cursor-pointer" data-testid="dashboard-link">
                      My Rentals
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer" data-testid="logout-button">
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button 
                  className="bg-[#1a1a1a] text-white hover:bg-[#333] font-bold uppercase tracking-wider text-sm px-6"
                  data-testid="login-button"
                >
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 hover:bg-gray-100 rounded"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link 
                to="/products" 
                className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                All Gear
              </Link>
              <Link 
                to="/how-it-works" 
                className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                How It Works
              </Link>
              <Link 
                to="/faq" 
                className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                FAQ
              </Link>
              <Link 
                to="/sizing-guide" 
                className="text-sm font-medium uppercase tracking-widest text-gray-600 hover:text-[#C6A87C] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sizing Guide
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
